import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { useFileUpload } from '@/hooks/use-file-upload';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import {
  CreateSpaceAdminValidationSchema,
  TCreateSpaceAdminValidation,
  useCreateSpaceAdminMutation,
} from '../../../api/admin/create-space-admin.api';
import { useTicketingModalRootActions } from '@/features/ticketings/_shared/components/ticketing-modal-root/ticketing-modal-root.store';
import { GET_LIST_SPACE_ADMIN_QUERY_KEY } from '../../../api/admin/get-list-space-admin.api';
import { GET_LIST_SPACES_QUERY_KEY } from '../../../api/get-list-space.api';

// ========== Constants ==========
const TOAST_MESSAGES = {
  CREATE_ERROR: 'Error creating space, please try again!',
  CREATE_SUCCESS: 'Space created successfully',
} as const;

const FILE_UPLOAD_CONFIG = {
  ACCEPTED_FILE_TYPES: [
    '.png',
    '.webp',
    '.jpg',
    '.jpeg',
    '.gif',
    '.svg',
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/svg+xml',
  ],
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
} as const;

// ========== Hook ==========
const useCreateSpaceModal = () => {
  // ========== Stores & Hooks ==========
  const showToast = useCustomToast();
  const ticketingModalActions = useTicketingModalRootActions();

  // ========== Form ==========
  const form = useForm<TCreateSpaceAdminValidation>({
    resolver: zodResolver(CreateSpaceAdminValidationSchema),
    mode: 'onChange',
  });

  // ========== State ==========
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);

  // ========== File Upload ==========
  const {
    handleFileChange,
    clearFile,
    error: fileError,
  } = useFileUpload({
    acceptedFileTypes: [...FILE_UPLOAD_CONFIG.ACCEPTED_FILE_TYPES],
    maxFileSize: FILE_UPLOAD_CONFIG.MAX_FILE_SIZE,
    onFileSelect: (file) => {
      const previewUrl = URL.createObjectURL(file);
      setIconPreview(previewUrl);
      form.setValue('icon', file);
    },
  });

  // ========== Queries & Mutations ==========
  const mutationCreateSpace = useCreateSpaceAdminMutation({
    mutationConfig: {
      onError: (_error) => {
        const errorMessage = axiosExtractErrorMessage(_error) || TOAST_MESSAGES.CREATE_ERROR;
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        // Invalidate related queries
        Promise.all([
          queryClient.invalidateQueries({ queryKey: [GET_LIST_SPACE_ADMIN_QUERY_KEY] }),
          queryClient.invalidateQueries({ queryKey: [GET_LIST_SPACES_QUERY_KEY] }),
        ]);

        ticketingModalActions.closeModal('CreateSpaceModal');
        form.reset();

        showToast({
          message: TOAST_MESSAGES.CREATE_SUCCESS,
          type: 'success',
        });
      },
    },
  });

  // ========== Computed Values ==========
  const isOpenCreateSpaceModal = useMemo(
    () => ticketingModalActions.checkIsOpenModal('CreateSpaceModal'),
    [ticketingModalActions]
  );

  const isButtonLoading = useMemo(
    () => mutationCreateSpace.isPending || form.formState.isSubmitting,
    [mutationCreateSpace.isPending, form.formState.isSubmitting]
  );

  // ========== Effects ==========
  // Clear file preview when modal closes
  useEffect(() => {
    if (!isOpenCreateSpaceModal) {
      clearFile();
      setIconPreview(null);
      form.setValue('icon', undefined);
    }
  }, [isOpenCreateSpaceModal, clearFile, form]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (iconPreview) {
        URL.revokeObjectURL(iconPreview);
      }
    };
  }, [iconPreview]);

  // ========== Handlers ==========
  const handleCloseModal = useCallback(() => {
    ticketingModalActions.closeModal('CreateSpaceModal');
  }, [ticketingModalActions]);

  const handleSubmitForm = useCallback(
    (data: TCreateSpaceAdminValidation) => {
      mutationCreateSpace.mutate({
        name: data.name,
        description: data.description || undefined,
        icon: data.icon || undefined,
      });
    },
    [mutationCreateSpace]
  );

  const handleIconClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveIcon = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      clearFile();
      setIconPreview(null);
      form.setValue('icon', undefined);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [clearFile, form]
  );

  const handleClose = useCallback(() => {
    handleCloseModal();
    form.reset();
  }, [handleCloseModal, form]);

  // ========== Return ==========
  return {
    // Form
    form,

    // Modal State
    isOpenCreateSpaceModal,
    isDisabledButton: isButtonLoading,
    isLoadingButton: isButtonLoading,

    // File Upload
    fileInputRef,
    iconPreview,
    fileError,
    handleFileChange,
    handleIconClick,
    handleRemoveIcon,

    // Handlers
    handleCloseModal,
    handleSubmitForm,
    handleClose,
  };
};

export default useCreateSpaceModal;
